import React from 'react';
import { pdf } from '@react-pdf/renderer';
import PersonalityTestPDF from '../components/common/PersonalityTestPDF';
import { Exercise } from '../utils/category_mappings';

interface GeneratePDFOptions {
  answers: number[];
  categoryScores: Record<string, number>;
  dominantCategory: string;
  personalInfo?: Record<string, string>;
  exercises?: Exercise[];
  userName?: string;
}

/**
 * Generate PDF blob from React component
 */
export const generatePDFBlob = async (options: GeneratePDFOptions): Promise<Blob> => {
  const document = (
    <PersonalityTestPDF
      answers={options.answers}
      categoryScores={options.categoryScores}
      dominantCategory={options.dominantCategory}
      personalInfo={options.personalInfo}
      exercises={options.exercises}
      userName={options.userName}
    />
  );
  const blob = await pdf(document).toBlob();
  return blob;
};

/**
 * Generate and download PDF locally
 */
export const downloadPDF = async (options: GeneratePDFOptions, filename?: string): Promise<void> => {
  try {
    const blob = await generatePDFBlob(options);
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `financial-personality-profile-${new Date().toISOString().split('T')[0]}.pdf`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('✅ PDF downloaded successfully');
  } catch (error) {
    console.error('❌ Error downloading PDF:', error);
    throw error;
  }
};

/**
 * Generate PDF and save to browser's local storage (as base64)
 */
export const savePDFToLocalStorage = async (options: GeneratePDFOptions, key?: string): Promise<string> => {
  try {
    const blob = await generatePDFBlob(options);
    
    // Convert blob to base64
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1]; // Remove data:application/pdf;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
    });
    
    reader.readAsDataURL(blob);
    const base64Data = await base64Promise;
    
    // Save to localStorage
    const storageKey = key || `pdf-${Date.now()}`;
    localStorage.setItem(storageKey, base64Data);
    
    console.log(`✅ PDF saved to localStorage with key: ${storageKey}`);
    return storageKey;
  } catch (error) {
    console.error('❌ Error saving PDF to localStorage:', error);
    throw error;
  }
};

/**
 * Retrieve PDF from localStorage and download it
 */
export const downloadPDFFromLocalStorage = (key: string, filename?: string): void => {
  try {
    const base64Data = localStorage.getItem(key);
    if (!base64Data) {
      throw new Error(`No PDF found with key: ${key}`);
    }
    
    // Convert base64 back to blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    // Download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `saved-pdf-${key}.pdf`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    console.log('✅ PDF downloaded from localStorage');
  } catch (error) {
    console.error('❌ Error downloading PDF from localStorage:', error);
    throw error;
  }
}; 